import React, { useEffect, useRef, useState } from 'react';

/**
 * 滚动入场动画组件
 * 当元素进入视口时，触发从下往上淡入的动画。
 * 
 * @param {React.ReactNode} children - 子元素
 * @param {string} className - 附加 class
 * @param {number} delay - 每个子元素的延迟 (ms)，用于行内卡片依次出场
 * @param {number} threshold - 触发阈值 0~1
 * @param {boolean} rootMargin - 提前触发距离
 * @param {boolean} once - 是否只触发一次（默认 true）
 * @param {'div'|'section'} tag - 包裹标签
 */
const ScrollReveal = ({
  children,
  className = '',
  delay = 0,
  stagger = 80,
  threshold = 0.15,
  rootMargin = '0px 0px -40px 0px',
  once = true,
  tag: Tag = 'div',
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (once) observer.unobserve(el);
        } else if (!once) {
          setVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  // 如果 children 是数组并需要 stagger 效果
  if (stagger > 0 && React.Children.count(children) > 1) {
    return (
      <Tag ref={ref} className={className}>
        {React.Children.map(children, (child, i) => (
          <div
            className={`scroll-reveal${visible ? ' visible' : ''}`}
            style={{ transitionDelay: `${delay + i * stagger}ms` }}
          >
            {child}
          </div>
        ))}
      </Tag>
    );
  }

  return (
    <Tag
      ref={ref}
      className={`scroll-reveal ${className}${visible ? ' visible' : ''}`}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
    >
      {children}
    </Tag>
  );
};

export default ScrollReveal;
